import { FilesClass } from 'src/app/shared/models/uploads-class';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, finalize, map, share, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ProductsProposalsClass,
  ProductsProposalsDetailsClass,
  ProductsProposalsTemplateClass,
  ProductsTypesProposalsPaymentsFilter,
  SafesimulationsClass,
  SafeSimulationsMessages,
  SafeSimulationsMessagesFiles,
  SafeSimulationsStatusTypes,
} from '../models/safesimulations-class';
import { Alert } from '../models/alert-class';
import { ProductsProposalsPaymentsSplitClass } from '../models/payments-class';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { FacebookPixelService } from './facebook-pixel.service';
import { GoogleAnalyticsService } from './google-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class SafesimulationsService {
  constructor(
    private http: HttpClient,
    public httpclienterrorService: HttpclienterrorService,
    private fbPixel: FacebookPixelService,
    private ga: GoogleAnalyticsService,
  ) {}

  private readonly splitPricesInFlight$ = new Map<number, Observable<ProductsProposalsPaymentsSplitClass[]>>();

  GetProductsTypesProposalsPaymentsSplitPricesALL(
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsPaymentsSplitClass[]> {
    // 1) Se já existe um pedido em curso para este id, reutiliza-o
    const existing = this.splitPricesInFlight$.get(idProductsTypesProposals);
    if (existing) return existing;

    const url = `${environment.URL}/safesimulations/GetProductsTypesProposalsPaymentsSplitPricesALL/${idProductsTypesProposals}`;

    // 2) Cria o pedido HTTP normal…
    const req$ = this.http.get<ProductsProposalsPaymentsSplitClass[]>(url).pipe(
      // 3) …partilhado: múltiplos subscribers usam a MESMA subscrição
      //    e, após completar/erro, o stream “resetta” (não mantém cache).
      share({
        connector: () => new ReplaySubject<ProductsProposalsPaymentsSplitClass[]>(1),
        resetOnError: true,
        resetOnComplete: true,
        resetOnRefCountZero: true,
      }),

      // 4) Mantém o teu tratamento de erro (mesma mensagem/string)
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),

      // 5) Remove do in-flight quando terminar (sucesso ou erro)
      finalize(() => {
        this.splitPricesInFlight$.delete(idProductsTypesProposals);
      }),
    );

    // Guarda no in-flight antes de devolver
    this.splitPricesInFlight$.set(idProductsTypesProposals, req$);
    return req$;
  }

  //   GetProductsTypesProposalsPaymentsSplitPricesALL(
  //     idProductsTypesProposals: number,
  //   ): Observable<ProductsProposalsPaymentsSplitClass[]> {
  //     return this.http
  //       .get<
  //         ProductsProposalsPaymentsSplitClass[]
  //       >(environment.URL + `/safesimulations/GetProductsTypesProposalsPaymentsSplitPricesALL/${idProductsTypesProposals}`)
  //       .pipe(
  //         catchError(error => {
  //           const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
  //           return throwError(() => errorMessage);
  //         }),
  //       );
  //   }

  GetProductsTypesProposalsPaymentsFilterSplit(
    idProductsTypesProposalsFilter: number,
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsPaymentsSplitClass[]> {
    return this.http
      .get<
        ProductsProposalsPaymentsSplitClass[]
      >(environment.URL + `/safesimulations/GetProductsTypesProposalsPaymentsFilterSplit/${idProductsTypesProposalsFilter}/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesProposalsPaymentsFilter(
    productsTypesProposalsPaymentsFilter: ProductsTypesProposalsPaymentsFilter,
    uuid: string,
  ): Observable<ProductsTypesProposalsPaymentsFilter[]> {
    productsTypesProposalsPaymentsFilter.UUIDLastChange = uuid;

    return this.http
      .post<
        ProductsTypesProposalsPaymentsFilter[]
      >(environment.URL + `/safesimulations/ManagementSetProductsTypesProposalsPaymentsFilter`, productsTypesProposalsPaymentsFilter)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductsTypesProposalsPaymentsFilterSplitALL(
    idProductsTypesProposalsFilter: number,
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsPaymentsSplitClass[]> {
    return this.http
      .get<
        ProductsProposalsPaymentsSplitClass[]
      >(environment.URL + `/safesimulations/GetProductsTypesProposalsPaymentsFilterSplitALL/${idProductsTypesProposalsFilter}/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductsTypesProposalsPaymentsFilterTree(
    idProductsTypesProposals: number,
  ): Observable<ProductsTypesProposalsPaymentsFilter[]> {
    return this.http
      .get<
        ProductsTypesProposalsPaymentsFilter[]
      >(environment.URL + `/safesimulations/GetProductsTypesProposalsPaymentsFilterTree/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductsTypesProposalsPaymentsFilter(
    idProductsTypesProposals: number,
  ): Observable<ProductsTypesProposalsPaymentsFilter[]> {
    return this.http
      .get<
        ProductsTypesProposalsPaymentsFilter[]
      >(environment.URL + `/safesimulations/GetProductsTypesProposalsPaymentsFilter/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductsTypesProposalsPaymentsFilterID0(
    idProductsTypesProposalsFilter: number,
  ): Observable<ProductsTypesProposalsPaymentsFilter[]> {
    return this.http
      .get<
        ProductsTypesProposalsPaymentsFilter[]
      >(environment.URL + `/safesimulations/GetProductsTypesProposalsPaymentsFilterID0/${idProductsTypesProposalsFilter}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesProposalsPaymentsSplit(
    productsProposalsPaymentsSplitClass: ProductsProposalsPaymentsSplitClass,
    uuid: string,
  ): Observable<ProductsProposalsPaymentsSplitClass> {
    productsProposalsPaymentsSplitClass.UUIDLastChange = uuid;

    return this.http
      .post<ProductsProposalsPaymentsSplitClass>(
        environment.URL + `/safesimulations/ManagementSetProductsTypesProposalsPaymentsSplit`,
        productsProposalsPaymentsSplitClass,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductsProposalsPaymentsSplitTypes(
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsPaymentsSplitClass[]> {
    return this.http
      .get<
        ProductsProposalsPaymentsSplitClass[]
      >(environment.URL + `/safesimulations/GetProductsProposalsPaymentsSplitTypes/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductsProposalsPaymentsSplitTypesALL(
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsPaymentsSplitClass[]> {
    return this.http
      .get<
        ProductsProposalsPaymentsSplitClass[]
      >(environment.URL + `/safesimulations/GetProductsProposalsPaymentsSplitTypesALL/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaSetSafeSimulationsMessages(
    safeSimulationsMessages: SafeSimulationsMessages,
    uuid: string,
  ): Observable<SafeSimulationsMessages[]> {
    safeSimulationsMessages.UUIDLastChange = uuid;

    return this.http
      .post<
        SafeSimulationsMessages[]
      >(environment.URL + `/safesimulations/ClientAreaSetSafeSimulationsMessages`, safeSimulationsMessages)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsMessages(idSafeSimulations: number): Observable<SafeSimulationsMessages[]> {
    return this.http
      .get<
        SafeSimulationsMessages[]
      >(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsMessages/${idSafeSimulations}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsMessagesFiles(
    idSafeSimulationsMessage: number,
  ): Observable<SafeSimulationsMessagesFiles[]> {
    return this.http
      .get<
        SafeSimulationsMessagesFiles[]
      >(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsMessagesFiles/${idSafeSimulationsMessage}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaCheckDatasafesimulations(idCustomer: number): Observable<Alert[]> {
    return this.http
      .get<Alert[]>(environment.URL + `/safesimulations/ClientAreaCheckDatasafesimulations/${idCustomer}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  CommercialAreaGetSafeSimulations(idCommercial: number): Observable<SafesimulationsClass[]> {
    return this.http
      .get<
        SafesimulationsClass[]
      >(environment.URL + `/safesimulations/CommercialAreaGetSafeSimulations/${idCommercial}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetSafeSimulationsStatusTypes(): Observable<SafeSimulationsStatusTypes[]> {
    return this.http
      .get<SafeSimulationsStatusTypes[]>(environment.URL + `/safesimulations/GetSafeSimulationsStatusTypes`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesTemplate(
    productsProposalsTemplateClass: ProductsProposalsTemplateClass,
    uuid: string,
  ): Observable<ProductsProposalsTemplateClass[]> {
    productsProposalsTemplateClass.UUIDLastChange = uuid;

    return this.http
      .post<
        ProductsProposalsTemplateClass[]
      >(environment.URL + `/safesimulations/ManagementSetProductsTypesTemplate`, productsProposalsTemplateClass)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposalsDetailsPDF(
    idProductsTypes: number,
    idProductsTypesTemplate: number,
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsDetailsClass[]> {
    return this.http
      .get<
        ProductsProposalsDetailsClass[]
      >(environment.URL + `/safesimulations/GetProductProposalsDetailsPDF/${idProductsTypes}/${idProductsTypesTemplate}/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposalsDetails(
    idProductsTypes: number,
    idProductsTypesProposals: number,
  ): Observable<ProductsProposalsDetailsClass[]> {
    return this.http
      .get<
        ProductsProposalsDetailsClass[]
      >(environment.URL + `/safesimulations/GetProductProposalsDetails/${idProductsTypes}/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposalsDetailsFiles(idProductsTypesProposalsDetails: number): Observable<FilesClass[]> {
    return this.http
      .get<
        FilesClass[]
      >(environment.URL + `/safesimulations/GetProductProposalsDetailsFiles/${idProductsTypesProposalsDetails}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposalsDetailsFilesIdPDF(idProductsTypesProposals: number): Observable<FilesClass[]> {
    return this.http
      .get<
        FilesClass[]
      >(environment.URL + `/safesimulations/GetProductProposalsDetailsFilesIdPDF/${idProductsTypesProposals}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetProductsTypesProposalsDetailsID(
    idProductsTypesProposalsDetails: number,
  ): Observable<ProductsProposalsDetailsClass> {
    return this.http
      .get<ProductsProposalsDetailsClass>(
        environment.URL +
          `/safesimulations/ManagementGetProductsTypesProposalsDetailsID/${idProductsTypesProposalsDetails}`,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesProposalsDetails(
    productsProposalsDetailsClass: ProductsProposalsDetailsClass,
    uuid: string,
  ): Observable<ProductsProposalsDetailsClass> {
    productsProposalsDetailsClass.UUIDLastChange = uuid;

    return this.http
      .post<ProductsProposalsDetailsClass>(
        environment.URL + `/safesimulations/ManagementSetProductsTypesProposalsDetails`,
        productsProposalsDetailsClass,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesProposalsDetailsFile(
    productsProposalsDetailsFilesClass: FilesClass[],
  ): Observable<FilesClass[]> {
    return this.http
      .post<
        FilesClass[]
      >(environment.URL + `/safesimulations/ManagementSetProductsTypesProposalsDetailsFile`, productsProposalsDetailsFilesClass)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetProductsTypesProposalsID(idProductsTypesProposals: number): Observable<ProductsProposalsClass> {
    return this.http
      .get<ProductsProposalsClass>(
        environment.URL + `/safesimulations/ManagementGetProductsTypesProposalsID/${idProductsTypesProposals}`,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesProposals(
    productsProposalsClass: ProductsProposalsClass,
    uuid: string,
  ): Observable<ProductsProposalsClass> {
    productsProposalsClass.UUIDLastChange = uuid;

    return this.http
      .post<ProductsProposalsClass>(
        environment.URL + `/safesimulations/ManagementSetProductsTypesProposals`,
        productsProposalsClass,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementSetProductsTypesProposalsDuplicate(inidProductsTypesProposals: number): Observable<ProductsProposalsClass> {
    let dados = {
      idProductsTypesProposals: inidProductsTypesProposals,
    };

    return this.http
      .post<ProductsProposalsClass>(
        environment.URL + `/safesimulations/ManagementSetProductsTypesProposalsDuplicate`,
        dados,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposalsID(idProductsTypesProposals: number): Observable<ProductsProposalsClass[]> {
    return this.http
      .get<
        ProductsProposalsClass[]
      >(environment.URL + `/safesimulations/GetProductProposalsID/${idProductsTypesProposals}/`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetProductProposals(idProductsTypes: number): Observable<ProductsProposalsClass[]> {
    return this.http
      .get<
        ProductsProposalsClass[]
      >(environment.URL + `/safesimulations/ManagementGetProductProposals/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposals(
    idProductsTypes: number,
    idCommonNIFType: number,
    vlComissoes: number,
  ): Observable<ProductsProposalsClass[]> {
    return this.http
      .get<
        ProductsProposalsClass[]
      >(environment.URL + `/safesimulations/GetProductProposals/${idProductsTypes}/${idCommonNIFType}/${vlComissoes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetProductProposalsTemplate(idProductsTypes: number): Observable<ProductsProposalsTemplateClass[]> {
    return this.http
      .get<
        ProductsProposalsTemplateClass[]
      >(environment.URL + `/safesimulations/GetProductProposalsTemplate/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetProductProposalsTemplateID(
    idProductsTypesTemplate: number,
  ): Observable<ProductsProposalsTemplateClass[]> {
    return this.http
      .get<
        ProductsProposalsTemplateClass[]
      >(environment.URL + `/safesimulations/ManagementGetProductProposalsTemplateID/${idProductsTypesTemplate}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ManagementGetProductsTypesTemplate(idProductsTypes: number): Observable<ProductsProposalsTemplateClass[]> {
    return this.http
      .get<
        ProductsProposalsTemplateClass[]
      >(environment.URL + `/safesimulations/ManagementGetProductsTypesTemplate/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsCustomerAll(idCustomer: number): Observable<SafesimulationsClass[]> {
    return this.http
      .get<
        SafesimulationsClass[]
      >(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsCustomerAll/${idCustomer}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsCustomerAllWidget(idCustomer: number): Observable<SafesimulationsClass[]> {
    return this.http
      .get<
        SafesimulationsClass[]
      >(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsCustomerAllWidget/${idCustomer}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsID(idSafeSimulations: number): Observable<SafesimulationsClass[]> {
    return this.http
      .get<
        SafesimulationsClass[]
      >(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsID/${idSafeSimulations}`)
      .pipe(
        map(safesimulations => {
          // Manipula os dados retornados para transformar os arrays em strings JSON
          // Usando a propriedade correta com JSON.stringify
          safesimulations.forEach(simulation => {
            // Verifica se 'fracionamentoSeleccaoMultiplaString' é uma string e converte para array
            // Verifica se 'fracionamentoSeleccaoMultiplaString' é uma string e converte para array
            if (
              simulation.fracionamentoSeleccaoMultiplaString &&
              typeof simulation.fracionamentoSeleccaoMultiplaString === 'string'
            ) {
              try {
                // Remove caracteres de escape, substitui aspas simples por aspas duplas e remove espaços desnecessários
                const cleanedString = simulation.fracionamentoSeleccaoMultiplaString
                  .replace(/\\"/g, '"') // Remove escapes de aspas duplas
                  .replace(/'/g, '"') // Substitui aspas simples por aspas duplas
                  .trim(); // Remove espaços no início e no fim

                // Verifica se a string começa com '[' e termina com ']' (formato de array JSON)
                if (cleanedString.startsWith('[') && cleanedString.endsWith(']')) {
                  const parsedArray = JSON.parse(cleanedString); // Converte a string limpa para array
                  simulation.FracionamentoSeleccaoMultipla = parsedArray; // Atribui o array resultante
                } else {
                  // console.error('fracionamentoSeleccaoMultiplaString não é um array JSON válido:', cleanedString);
                  simulation.FracionamentoSeleccaoMultipla = []; // Atribui um array vazio se o formato for inválido
                }
              } catch (error) {
                // console.error('Erro ao converter fracionamentoSeleccaoMultiplaString em array:', error);
                simulation.FracionamentoSeleccaoMultipla = []; // Atribui um array vazio em caso de erro
              }
            } else {
              // Se não for uma string válida, define como array vazio
              simulation.FracionamentoSeleccaoMultipla = [];
            }

            if (
              simulation.FormaPagamentoSeleccaoMultiplaString &&
              typeof simulation.FormaPagamentoSeleccaoMultiplaString === 'string'
            ) {
              try {
                // Remove caracteres de escape e espaços desnecessários
                const cleanedFormaPagamentoString = simulation.FormaPagamentoSeleccaoMultiplaString.replace(/\\"/g, '"') // Remove escapes de aspas duplas
                  .replace(/'/g, '"') // Substitui aspas simples por aspas duplas
                  .trim(); // Remove espaços no início e no fim

                // Verifica se a string começa com '[' e termina com ']' (formato de array JSON)
                if (cleanedFormaPagamentoString.startsWith('[') && cleanedFormaPagamentoString.endsWith(']')) {
                  const parsedFormaPagamentoArray = JSON.parse(cleanedFormaPagamentoString);
                  simulation.FormaPagamentoSeleccaoMultipla = parsedFormaPagamentoArray;
                } else {
                  // console.error('FormaPagamentoSeleccaoMultiplaString não é um array JSON válido:', cleanedFormaPagamentoString);
                  simulation.FormaPagamentoSeleccaoMultipla = [];
                }
              } catch (error) {
                // console.error('Erro ao converter FormaPagamentoSeleccaoMultiplaString em array:', error);
                simulation.FormaPagamentoSeleccaoMultipla = [];
              }
            } else {
              // Se não for uma string válida, define como array vazio
              simulation.FormaPagamentoSeleccaoMultipla = [];
            }

            if (
              simulation.AutoCivilLiabilityOptionString &&
              typeof simulation.AutoCivilLiabilityOptionString === 'string'
            ) {
              try {
                // Remove caracteres de escape, substitui aspas simples por aspas duplas e remove espaços desnecessários
                const cleanedAutoCivilLiabilityOptionString = simulation.AutoCivilLiabilityOptionString.replace(
                  /\\"/g,
                  '"',
                ) // Remove escapes de aspas duplas
                  .replace(/'/g, '"') // Substitui aspas simples por aspas duplas
                  .trim(); // Remove espaços no início e no fim

                // Verifica se a string começa com '[' e termina com ']' (formato de array JSON)
                if (
                  cleanedAutoCivilLiabilityOptionString.startsWith('[') &&
                  cleanedAutoCivilLiabilityOptionString.endsWith(']')
                ) {
                  const parsedAutoCivilLiabilityOptionArray = JSON.parse(cleanedAutoCivilLiabilityOptionString); // Converte a string limpa para array
                  simulation.AutoCivilLiabilityOption = parsedAutoCivilLiabilityOptionArray; // Atribui o array resultante
                } else {
                  simulation.AutoCivilLiabilityOption = []; // Atribui um array vazio se o formato for inválido
                }
              } catch (error) {
                simulation.AutoCivilLiabilityOption = []; // Atribui um array vazio em caso de erro
              }
            } else {
              // Se não for uma string válida, define como array vazio
              simulation.AutoCivilLiabilityOption = [];
            }
          });

          // console.log(safesimulations);
          return safesimulations;
        }),
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsContinue(
    idCustomer: number,
    idProduct: number,
    idProductsTypes: number,
  ): Observable<SafesimulationsClass[]> {
    return this.http
      .get<
        SafesimulationsClass[]
      >(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsContinue/${idCustomer}/${idProduct}/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetSafeSimulationsFilesID(idSafeSimulations: number): Observable<FilesClass[]> {
    return this.http
      .get<FilesClass[]>(environment.URL + `/safesimulations/ClientAreaGetSafeSimulationsFilesID/${idSafeSimulations}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaSetSafeSimulations(
    safesimulationsClass: SafesimulationsClass,
    uuid: string,
  ): Observable<SafesimulationsClass> {
    // tslint:disable-next-line:object-literal-shorthand

    // console.log(safesimulationsClass);
    safesimulationsClass.UUIDLastChange = uuid;
    safesimulationsClass.JSONProductsTypesPurchaseConfigStepperComponentClass = null;
    safesimulationsClass.productsProposalsClassPurchaseClass = null;
    safesimulationsClass.JSONProposal = null;
    safesimulationsClass.productsProposalsClass = null;
    safesimulationsClass.configStepperClass = null;
    safesimulationsClass.productsProposalsTemplateClass = null;
    safesimulationsClass.productsTypesPurchaseConfigPDFClass = null;

    // console.log('ClientAreaSetSafeSimulations serviço');
    // console.log('gravar');
    return this.http
      .post<SafesimulationsClass>(
        environment.URL + `/safesimulations/ClientAreaSetSafeSimulations`,
        safesimulationsClass,
      )
      .pipe(
        tap(result => {
          // Track Meta Pixel AddToCart - Simulação criada (equivale a "adicionar ao carrinho")
          if (result && result.idSafeSimulations) {
            this.fbPixel.trackAddToCart(
              result.Name || 'Simulação',
              'sim_' + result.idSafeSimulations,
              0, // Valor será atualizado quando houver proposta
            );
            this.ga.trackAddToCart(result.Name || 'Simulação', 'sim_' + result.idSafeSimulations, 0);
          }
        }),
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaSetSafeSimulationsFiles(safesimulationsFilesClass: FilesClass, uuid: string): Observable<FilesClass> {
    // tslint:disable-next-line:object-literal-shorthand

    // console.log(safesimulationsClass);
    safesimulationsFilesClass.UUIDLastChange = uuid;

    // console.log('ClientAreaSetSafeSimulations serviço');
    // console.log('gravar');
    return this.http
      .post<FilesClass>(
        environment.URL + `/safesimulations/ClientAreaSetSafeSimulationsFiles`,
        safesimulationsFilesClass,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  /**
   * Download Excel template for prices
   * @param idProductsTypesProposals - ID of the proposal
   * @param includeData - If true, includes existing price data in the template
   * @returns Observable<Blob> - Excel file as Blob
   */
  downloadPriceTemplate(idProductsTypesProposals: number, includeData: boolean = false): Observable<Blob> {
    const url = `${environment.URL}/safesimulations/ExportPriceTemplate/${idProductsTypesProposals}?includeData=${includeData ? '1' : '0'}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  /**
   * Upload Excel file with prices
   * @param idProductsTypesProposals - ID of the proposal
   * @param file - Excel file to upload
   * @returns Observable with import result
   */
  uploadPrices(
    idProductsTypesProposals: number,
    file: File,
  ): Observable<{
    success: boolean;
    message: string;
    rowsProcessed?: number;
    pricesUpdated?: number;
    filtersCreated?: number;
    errors?: string[];
  }> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const url = `${environment.URL}/safesimulations/ImportPrices/${idProductsTypesProposals}`;
    return this.http
      .post<{
        success: boolean;
        message: string;
        rowsProcessed?: number;
        pricesUpdated?: number;
        filtersCreated?: number;
        errors?: string[];
      }>(url, formData)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }
}
